import { UniversalLink } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import React, { useEffect, useRef, useState } from 'react';
import {
  MPFButton,
  MPFSectionHeading,
  NewsCardRow,
} from 'volto-ministerio-publico-federal/components/UI';
import { SidebarLinkCard } from 'volto-ministerio-publico-federal/components/UI/SidebarLinkCard';
import type { NewsCardItem } from 'volto-ministerio-publico-federal/components/UI';
import {
  NEWS_PORTAL_TYPES,
  NEWS_PUBLIC_STATES,
} from 'volto-ministerio-publico-federal/constants';

type RelatedItemBase = {
  '@id': string;
  '@type': string;
  UID?: string;
  title: string;
  description?: string;
  effective?: string;
  type_title?: string;
  review_state?: string;
};

export type RelatedNewsItem = RelatedItemBase &
  NewsCardItem & {
    /** `News` é o tipo canônico; os outros dois são o legado ainda não migrado. */
    '@type': 'News' | 'News Item' | 'Noticia';

    Subject?: string[];

    parent?: string;
  };

/**
 * Qualquer conteúdo relacionado que não seja notícia — página, pasta, arquivo,
 * imagem. O seletor do formulário aceita o portal inteiro, então isto não é
 * exceção: é o caso comum de quem relaciona uma página de serviço à notícia.
 */
type RelatedLinkItem = RelatedItemBase;

export type RelatedItem = RelatedNewsItem | RelatedLinkItem;

interface RelatedNewsProps {
  relatedItems?: RelatedItem[];
  /** `false` esconde a unidade nos cards (uso na página de notícia). */
  showUnit?: boolean;
  /** Pasta que contém a notícia — destino do link quando não há mais itens. */
  parent?: { '@id'?: string; title?: string };
}

/** Quantidade de relacionadas exibidas por vez. */
const PAGE_SIZE = 3;

/**
 * `relatedItems` vem do campo do conteúdo, não do catálogo: nenhum filtro de
 * estado é aplicado no caminho. E o `mpf_noticias_workflow` dá `View` a
 * `Anonymous` também no estado `pending`, então uma notícia ainda em revisão que
 * alguém relacionou apareceria para o visitante. Passam só os dois estados
 * realmente publicados — "Publicado Nacional" (`published`) e "Publicado Local"
 * (`visible`). Quando o `review_state` não vem no payload, mantemos o item (não
 * dá para concluir nada a partir da ausência).
 */
const isPublished = (item: RelatedItem): boolean =>
  !item.review_state || NEWS_PUBLIC_STATES.includes(item.review_state);

const isNewsItem = (item: RelatedItem): item is RelatedNewsItem => {
  return NEWS_PORTAL_TYPES.includes(item['@type']) && isPublished(item);
};

/**
 * Notícias publicadas dentro de `relatedItems`, na ordem em que o editor as
 * relacionou.
 */
export const filterRelatedNews = (
  relatedItems: RelatedItem[] = [],
): RelatedNewsItem[] => relatedItems.filter(isNewsItem);

/**
 * O resto do que o editor relacionou: páginas, pastas, arquivos. Vira lista de
 * links porque não tem imagem, tema nem unidade para preencher um card de
 * notícia — mas sumir em silêncio, como acontecia antes, é pior: o editor
 * escolhe o item, salva e não vê nada na página.
 */
export const filterRelatedLinks = (
  relatedItems: RelatedItem[] = [],
): RelatedLinkItem[] =>
  relatedItems.filter((item) => !isNewsItem(item) && isPublished(item));

/**
 * Se a seção "Veja também" tem algo a mostrar. A `NewsView` usa isto para
 * decidir entre ela e o bloco automático de tema — duplicar a regra lá faria as
 * duas discordarem sobre o que conta como relacionado.
 */
export const hasRelatedContent = (relatedItems: RelatedItem[] = []): boolean =>
  filterRelatedNews(relatedItems).length > 0 ||
  filterRelatedLinks(relatedItems).length > 0;

const RelatedNews: React.FC<RelatedNewsProps> = ({
  relatedItems = [],
  parent,
  showUnit = true,
}) => {
  const relatedNews = filterRelatedNews(relatedItems);
  const relatedLinks = filterRelatedLinks(relatedItems);

  // O `@id` vem absoluto do backend — o flatten o torna relativo à app e o
  // UniversalLink garante navegação client-side em vez de reload.
  const parentUrl = parent?.['@id']
    ? flattenToAppURL(parent['@id'])
    : '/noticias';
  const parentTitle = parent?.title || 'Notícias';

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (loadTimer.current) clearTimeout(loadTimer.current);
    };
  }, []);

  // Os itens já vêm em `relatedItems`, então revelar mais é síncrono. O atraso
  // curto existe só para o clique ter retorno visual; se um dia a paginação
  // virar requisição ao backend, é aqui que ela entra.
  const handleLoadMore = () => {
    setLoadingMore(true);
    loadTimer.current = setTimeout(() => {
      setVisibleCount((count) => count + PAGE_SIZE);
      setLoadingMore(false);
    }, 250);
  };

  // "há X" depende de Date.now(), que difere entre o render do servidor e a
  // hidratação no cliente — só formatamos como relativo após o mount.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  if (relatedNews.length === 0 && relatedLinks.length === 0) {
    return null;
  }

  const visibleNews = relatedNews.slice(0, visibleCount);
  const hasMore = visibleCount < relatedNews.length;

  return (
    <section className="mpf-news__related">
      <MPFSectionHeading title="Veja também" />

      {visibleNews.length > 0 && (
        <div className="mpf-news__related-body">
          {visibleNews.map((item) => (
            <NewsCardRow
              key={item.UID || item['@id']}
              item={item}
              baseClass="mpf-news__related"
              showRelativeDate={mounted}
              showUnit={showUnit}
            />
          ))}
        </div>
      )}

      {relatedLinks.length > 0 && (
        <div className="mpf-news__related-links">
          <SidebarLinkCard
            title="Outros conteúdos"
            as="h3"
            items={relatedLinks.map((item) => ({
              key: item.UID || item['@id'],
              href: flattenToAppURL(item['@id']),
              label: item.title,
            }))}
            emptyMessage=""
          />
        </div>
      )}

      <div className="mpf-news__related-footer">
        {hasMore ? (
          <MPFButton
            variant="primary"
            size="xl"
            fullWidth
            loading={loadingMore}
            onClick={handleLoadMore}
          >
            {loadingMore ? 'Carregando…' : 'Mostrar mais'}
          </MPFButton>
        ) : (
          <MPFButton
            as={UniversalLink}
            href={parentUrl}
            variant="primary"
            size="xl"
            fullWidth
          >
            Ir para {parentTitle}
          </MPFButton>
        )}
      </div>
    </section>
  );
};

export default RelatedNews;
